// src/components/CustomerFormModal.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { vehicleData } from '../data/vehicleData';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Divider, CircularProgress } from '@mui/material';

const formatPlaka = (value) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    let result = '';
    const numbers = cleaned.match(/\d+/g) || [];
    const letters = cleaned.match(/[A-Z]+/g) || [];
    if (numbers.length > 0) result += numbers[0].substring(0, 2);
    if (letters.length > 0) { if (result.length > 0) result += ' '; result += letters[0].substring(0, 4); }
    if (numbers.length > 1) { if (result.length > 0 && !result.endsWith(' ')) result += ' '; result += numbers[1].substring(0, 4); }
    return result;
};

const CustomerFormModal = ({ isOpen, onClose, onAddCustomer, onUpdateCustomer, customerToEdit }) => {
    const [isSaving, setIsSaving] = useState(false);
    // Customer fields
    const [adSoyad, setAdSoyad] = useState('');
    const [telefon, setTelefon] = useState('');
    const [email, setEmail] = useState('');
    const [adres, setAdres] = useState('');
    // Vehicle fields
    const [addVehicle, setAddVehicle] = useState(false);
    const [plaka, setPlaka] = useState('');
    const [marka, setMarka] = useState('');
    const [model, setModel] = useState('');
    const [yil, setYil] = useState('');
    const [renk, setRenk] = useState('');

    useEffect(() => {
        if (isOpen && customerToEdit) {
            setAdSoyad(customerToEdit.adSoyad);
            setTelefon(customerToEdit.telefon);
            setEmail(customerToEdit.email || '');
            setAdres(customerToEdit.adres || '');
            setAddVehicle(false); // Düzenleme modunda araç ekleme kapalı
        } else {
            setAdSoyad(''); setTelefon(''); setEmail(''); setAdres('');
            setAddVehicle(false); setPlaka(''); setMarka(''); setModel(''); setYil(''); setRenk('');
        }
    }, [customerToEdit, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!adSoyad || !telefon) {
            alert('Ad Soyad ve Telefon alanları zorunludur.');
            return;
        }
        if (addVehicle && (!plaka || !marka)) {
            alert('Araç eklemek için Plaka ve Marka alanları zorunludur.');
            return;
        }
        
        setIsSaving(true);
        const customerData = { adSoyad, telefon, email, adres };
        const vehicleInfo = { plaka: formatPlaka(plaka).trim(), marka, model, yil, renk };

        try {
            if (customerToEdit) {
                await onUpdateCustomer(customerToEdit.id, customerData);
            } else {
                // onAddCustomer artık vehicle bilgisini de alacak şekilde güncellenmeli.
                // Ana bileşendeki (Musteriler.js) fonksiyonu bu şekilde güncelleyeceğiz.
                await onAddCustomer(customerData, addVehicle ? vehicleInfo : null);
            }
            onClose();
        } catch (error) {
            console.error("Müşteri/Araç kaydedilirken hata:", error);
            alert("Bir hata oluştu.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;
    const isEditing = !!customerToEdit;

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Müşteri Bilgilerini Düzenle' : 'Yeni Müşteri Ekle'}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField label="Adı Soyadı" value={adSoyad} onChange={(e) => setAdSoyad(e.target.value)} fullWidth required />
                    <TextField label="Telefon Numarası" value={telefon} onChange={(e) => setTelefon(e.target.value)} fullWidth required />
                    <TextField label="E-posta Adresi" type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
                    <TextField label="Adres" value={adres} onChange={(e) => setAdres(e.target.value)} multiline rows={3} fullWidth />

                    {!isEditing && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <FormControlLabel control={<Checkbox checked={addVehicle} onChange={(e) => setAddVehicle(e.target.checked)}/>} label="Bu Müşteriye Araç Ekle" />
                            {addVehicle && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                                    <TextField label="Plaka" placeholder="Örn: 33 ABC 123" value={plaka} onChange={(e) => setPlaka(e.target.value)} fullWidth required />
                                    <FormControl fullWidth required><InputLabel>Marka</InputLabel>
                                        <Select value={marka} label="Marka" onChange={(e) => { setMarka(e.target.value); setModel(''); }}>
                                            {Object.keys(vehicleData).sort().map(brand => (<MenuItem key={brand} value={brand}>{brand}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth disabled={!marka}><InputLabel>Model</InputLabel>
                                        <Select value={model} label="Model" onChange={(e) => setModel(e.target.value)}>
                                            <MenuItem value=""><em>Model Seçiniz...</em></MenuItem>
                                            {marka && vehicleData[marka]?.map(modelObj => (<MenuItem key={modelObj.model} value={modelObj.model}>{modelObj.model}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                    <TextField label="Yıl" type="number" value={yil} onChange={e => setYil(e.target.value)} fullWidth />
                                    <TextField label="Renk" value={renk} onChange={e => setRenk(e.target.value)} fullWidth />
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose} disabled={isSaving}>İptal</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} /> : (isEditing ? 'Bilgileri Güncelle' : 'Kaydet')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerFormModal;